import React, { useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

import API from '../utils/api';

const DEPARTMENTS = [
  "Founder's Office",
  "Social & Content",
  "Video & Motion",
  "Creative & Design",
  "Paid & Performance"
];

const EMPLOYMENT_TYPES = ["Intern", "Full-time"];

export default function CreateEmployeeDialog({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    dob: '',
    department: '',
    role: '',
    employment_type: '',
    reporting_manager: '',
    status: 'Active',
    base_salary: 0,
    hra: 0,
    allowances: 0,
    bonus: 0,
    deductions: 0,
    aadhar: '',
    aadhar_verified: false,
    joining_date: '',
    review_date: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    const num = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [field]: num }));
  };

  const totalSalary = formData.base_salary + formData.hra + formData.allowances + formData.bonus - formData.deductions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/employees`, formData, { withCredentials: true });
      onCreated();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(', '));
      } else {
        setError(detail || 'Failed to create employee');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-2xl font-bold" data-testid="create-employee-dialog-title">
            Create Employee
          </DialogTitle>
          <p className="text-slate-500 text-sm">
            Employee ID will be auto-generated
          </p>
        </DialogHeader>

        {error && (
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm" data-testid="create-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Basic Details */}
          <FormSection title="Basic Details">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Full Name" required>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="John Doe"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-name-input"
                />
              </FormField>
              <FormField label="Phone" required>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  placeholder="+91 98765 43210"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-phone-input"
                />
              </FormField>
              <FormField label="Email" required>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  placeholder="john@company.com"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-email-input"
                />
              </FormField>
              <FormField label="Date of Birth" required>
                <Input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleChange('dob', e.target.value)}
                  required
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-dob-input"
                />
              </FormField>
            </div>
          </FormSection>

          {/* Job Details */}
          <FormSection title="Job Details">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Department" required>
                <Select value={formData.department} onValueChange={(v) => handleChange('department', v)}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="create-department-select">
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10">
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Role" required>
                <Input
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  required
                  placeholder="Software Engineer"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-role-input"
                />
              </FormField>
              <FormField label="Employment Type" required>
                <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="create-type-select">
                    <SelectValue placeholder="Select Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10">
                    {EMPLOYMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Reporting Manager">
                <Input
                  value={formData.reporting_manager}
                  onChange={(e) => handleChange('reporting_manager', e.target.value)}
                  placeholder="Manager Name"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-manager-input"
                />
              </FormField>
              <FormField label="Joining Date" required>
                <Input
                  type="date"
                  value={formData.joining_date}
                  onChange={(e) => handleChange('joining_date', e.target.value)}
                  required
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-joining-date-input"
                />
              </FormField>
            </div>
          </FormSection>

          {/* Salary Breakdown */}
          <FormSection title="Salary Breakdown">
            <div className="grid grid-cols-3 gap-4">
              <FormField label="Base Salary">
                <Input
                  type="number"
                  value={formData.base_salary}
                  onChange={(e) => handleNumberChange('base_salary', e.target.value)}
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="create-base-salary-input"
                />
              </FormField>
              <FormField label="HRA">
                <Input
                  type="number"
                  value={formData.hra}
                  onChange={(e) => handleNumberChange('hra', e.target.value)}
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="create-hra-input"
                />
              </FormField>
              <FormField label="Allowances">
                <Input
                  type="number"
                  value={formData.allowances}
                  onChange={(e) => handleNumberChange('allowances', e.target.value)}
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="create-allowances-input"
                />
              </FormField>
              <FormField label="Bonus">
                <Input
                  type="number"
                  value={formData.bonus}
                  onChange={(e) => handleNumberChange('bonus', e.target.value)}
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="create-bonus-input"
                />
              </FormField>
              <FormField label="Deductions">
                <Input
                  type="number"
                  value={formData.deductions}
                  onChange={(e) => handleNumberChange('deductions', e.target.value)}
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="create-deductions-input"
                />
              </FormField>
              <FormField label="Total Salary">
                <div className="h-10 px-3 flex items-center bg-emerald-500/10 border border-emerald-500/20 rounded-md font-mono text-emerald-400" data-testid="create-total-salary">
                  {formatCurrency(totalSalary)}
                </div>
              </FormField>
            </div>
          </FormSection>

          {/* Compliance */}
          <FormSection title="Compliance">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Aadhar Number">
                <Input
                  value={formData.aadhar}
                  onChange={(e) => handleChange('aadhar', e.target.value)}
                  placeholder="XXXX-XXXX-XXXX"
                  maxLength={12}
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="create-aadhar-input"
                />
              </FormField>
            </div>
          </FormSection>

          {/* Review Setup */}
          <FormSection title="Review">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Review Date">
                <Input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => handleChange('review_date', e.target.value)}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="create-review-date-input"
                />
              </FormField>
            </div>
          </FormSection>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="cancel-create-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]"
              data-testid="submit-create-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" size={16} />
                  Creating...
                </span>
              ) : (
                'Create Employee'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm text-slate-400 mb-2">
        {label} {required && <span className="text-rose-400">*</span>}
      </label>
      {children}
    </div>
  );
}
